import React, { useEffect, useRef, useState } from "react";

import { getBusinessEmployees } from "api/business-user-management";
import {
  IBusinessEmployeesResult,
  IGetBusinessEmployeesPayload,
} from "api/business-user-management/types";
import { useParams, useSearchParams } from "react-router-dom";
import { capitalizeFirstLetter } from "utils";
import { ZERO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA, Pagination } from "ui/pagination";
import { Status } from "ui/status";
import { TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";

import { BusinessEmployeesTableHeaderCTAs } from "./BusinessEmployeesTableHeaderCTAs";
import { EmployeeInfoPopUp } from "./EmployeeInfoPopUp";
import { DEFAULT_EMPLOYEES_TABLE_PAGINATION_PARAMS, columns } from "./const";
import { IEmployeeInfo } from "./types";

const lastColor: string | null = null;
export const BusinessEmployeesTable = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const search = searchParams.get("search");
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [businessEmployeesResult, setBusinessEmployeesResult] =
    useState<IBusinessEmployeesResult>({
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });
  const [selectedBusinessEmployeeIds, setSelectedBusinessEmployeeIds] =
    useState<TSelectedTableRowIds>({});

  const handleGetBusinessEmployeesData = async (
    payload: IGetBusinessEmployeesPayload
  ) => {
    setIsLoading(true);
    try {
      const { data, error, message } = await getBusinessEmployees(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setBusinessEmployeesResult(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoading(false);
  };

  const formattedData = (businessEmployeesResult?.data || []).map(
    (employee) => {
      const employeeUserAcctNum = employee?.user?.accountNumber || "N/A";
      const employeeUserTier = employee?.user?.accountTier;
      const employeeUserImage = employee?.user?.profileImage || "";
      const employeeUserId = employee?.user?.id || "";
      const employeeInfo: IEmployeeInfo = {
        fullName: `${employee?.firstName} ${employee?.lastName}`,
        level: employeeUserTier ? `Tier ${employeeUserTier}` : "N/A",
        primaryAcctNumber: employeeUserAcctNum,
        emailAddress: employee?.email || "N/A",
        phoneNumber: employee?.phoneNumber || "N/A",
        id: employeeUserId,
        imageUrl: employeeUserImage,
        tierNumber: employeeUserTier || ZERO,
      };

      let employeeStatus: TStatus = "error";
      switch (employee?.status) {
        case "active":
          employeeStatus = "success";
          break;
        case "pending":
          employeeStatus = "warning";
          break;
        case "disabled":
          employeeStatus = "disabled";
          break;
        default:
          break;
      }

      return {
        "Employee Name": (
          <EmployeeInfoPopUp
            employeeInfo={employeeInfo}
            lastColor={lastColor || ""}
          />
        ),
        "Phone Number": (
          <p className="text-sm text-[#667085]">
            {employee?.phoneNumber || "N/A"}
          </p>
        ),
        "Job Title": (
          <p className="text-sm text-[#667085]">
            {employee?.jobTitle || "N/A"}
          </p>
        ),
        "ODP Enrolment Status": employee?.status ? (
          <Status
            status={employeeStatus}
            text={capitalizeFirstLetter(employee?.status)}
            className="w-fit"
          />
        ) : (
          "N/A"
        ),
        id: employee?.id,
      };
    }
  );
  const { data: _, ...paginationData } = businessEmployeesResult;

  useEffect(() => {
    if (!businessId) return;
    const payload: IGetBusinessEmployeesPayload = {
      businessId,
      page: Number(page || DEFAULT_EMPLOYEES_TABLE_PAGINATION_PARAMS.page),
      limit: Number(limit || DEFAULT_EMPLOYEES_TABLE_PAGINATION_PARAMS.limit),
    };

    if (search) {
      payload["search"] = search;
    }
    void handleGetBusinessEmployeesData(payload);
  }, [businessId, page, limit, search]);

  // useEffect(
  //   () => console.log("businessEmployeesResult: ", businessEmployeesResult),
  //   [businessEmployeesResult]
  // );

  return (
    <TableWrapper className="!p-0">
      <BusinessEmployeesTableHeaderCTAs
      // handleGetBusinessEmployeesData={handleGetBusinessEmployeesData}
      // businessId={businessId || ""}
      />
      <Table
        columns={columns}
        data={formattedData}
        isLoading={isLoading}
        hasCheckbox
        selectedTableRowIds={selectedBusinessEmployeeIds}
        setSelectedTableRowIds={setSelectedBusinessEmployeeIds}
        tableRef={tableRef}
      />
      <Pagination
        defaultPaginationParams={DEFAULT_EMPLOYEES_TABLE_PAGINATION_PARAMS}
        paginationData={paginationData}
        data={businessEmployeesResult.data}
        tableRef={tableRef}
        isLoadingTable={isLoading}
      />
    </TableWrapper>
  );
};
